import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { Formik, Form } from 'formik';
import useUsers from '../../context/UsersContext';
import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import simpletaskService from '../../service/simpletask.service';
import appService from '../../service/app.service';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, DialogUI, ButtonUI, ConfirmDialogUI } from '../Interface';
import FormFields from './FormFields';
import SimpleTaskTabs from './SimpleTaskTabs';
import { TaskReportForm } from '../TaskReport/TaskReportForm';
import History from '../History/History';
import HistoryDialog from '../History/HistoryDialog';
import useConfig from '../../context/ConfigContext';
import { getDynVals } from '../../utils/DynHelper';
import { ResourceType } from '../../config/simpleTask';
import { generateDefaultTime } from '../../utils/helper';

export const SimpleTaskForm = ({ simpleTaskState, dialog }) => {
  const {
    setRefetchTable,
    selectedValues,
    setSelectedValues,
    viewField,
    setViewField,
    operatorsDisabled,
    setOperatorsDisabled,
    submenu,
    submenuNames,
    params,
    canEdit,
    autocompleteData,
    autocompleteOptions,
    canCopy,
    canDelete,
    editButtonVisible,
    setEditButtonvisible,
    setRowSelectionModel,
    dynLoading,
    setDynLoading,
    dynProps,
    setDynProps,
  } = simpleTaskState;
  const { simpleTaskId } = useParams();
  const { user, getRole } = useUsers();
  const { config } = useConfig();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addField, setAddField] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});
  const [roles, setRoles] = useState({});

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);

  const defaultRespPerson = {
    id: null,
    isNew: true,
    userId: null,
    roleName: '',
  };

  const defaultResource = {
    resourceTwoId: null,
    leader: false,
    employeeId: null,
    isNew: true,
  };

  const def = {
    simpleTaskName: '',
    startDate: dayjs(new Date()),
    endDate: dayjs(new Date()).add(1, 'hour'),
    duration: 1,
    preparedness: '',
    machineId: null,
    projectId: null,
    projectOrderNumber: '',
    simpleTaskTypeId: null,
    userId: null,
    resposiblePersonId: null,
    statusId: null,
    priorId: null,
    simpleTaskDescription: '',
    companyLocationId: null,
    resourceOneId: null,
    resourceTwoId: null,
    resourceThreeId: null,
    resourceFourId: null,
    simpleTaskRespPersonlist: [],
    simpleTaskAttachment: [],
    solutionProposal: '',
    companyId: null,
    resources: [],
    phoneNumber: '',
    joker_char_head1: '',
    finishedAt: null,
    plannedTime: '',
    quantityUnitId: null,
  };

  useEffect(() => {
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole(['SIMPLETASK_NOT_EDIT_RESPONSIBLE_FIELD', 'TASK_KANBAN_ONLY_KANBAN_STATUS_SELECT']);

      setRoles(roles);
    }
  }, [user]);

  useEffect(() => {
    if (Object.keys(config).length > 0) {
      setDefaultValues((prev) => {
        const startDate = generateDefaultTime(prev.startDate, config.defTaskFromTime);

        let endDate = prev.endDate;
        let duration = prev.duration;
        if (config.defTaskDuration) {
          endDate = dayjs(startDate).add(config.defTaskDuration, 'hour');
          duration = config.defTaskDuration;
        }

        return {
          ...prev,
          duration,
          startDate,
          endDate,
          quantityUnitId: config.defTaskQtyUnit ?? null,
        };
      });
    }
  }, [config]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          simpleTaskTypeId: autocompleteData?.type?.defaultValue ?? null,
          statusId: autocompleteData?.status?.defaultValue ?? null,
          priorId: autocompleteData?.priority?.defaultValue ?? null,
          ...(dialog && !dialog?.isUpdate && { projectId: dialog?.parentData?.projectId }),
          ...(dialog?.dialogData && {
            ...dialog?.dialogData,
            dynamicFields: getDynVals(dynProps, dialog?.dialogData.dynamicProperties),
          }),
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          type: autocompleteData?.type?.defItem,
          status: autocompleteData?.status?.defItem,
          priority: autocompleteData?.priority?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    if (simpleTaskId) {
      getRowById();
    } else {
      setSelectedValues(defaultSelectedValues);
    }
  }, [defaultSelectedValues, simpleTaskId]);

  useEffect(() => {
    setDefaultValues((prev) => {
      return { ...prev, userId: user.userId };
    });

    setDefaultSelectedValues((prev) => {
      return {
        ...prev,
        user: user,
      };
    });
  }, [user.userId]);

  const getRowById = () => {
    simpletaskService.getTaskById(simpleTaskId).then((data) => {
      setSelectedValues(data);
    });
  };

  const validationSchema = Yup.object().shape({
    simpleTaskName: Yup.string().required(),
    startDate: Yup.string().required(),
    endDate: Yup.string().required(),
    duration: Yup.number().positive(),
    preparedness: Yup.number().nullable().positive(),
    machineId: Yup.string().nullable(),
    projectId: Yup.string().nullable(),
    projectOrderNumber: Yup.string(),
    simpleTaskTypeId: Yup.string().required(),
    userId: Yup.string().required(),
    resposiblePersonId: Yup.string().nullable(),
    statusId: Yup.string().required(),
    priorId: Yup.string().required(),
    simpleTaskDescription: Yup.string().nullable().max(3000),
    companyLocationId: Yup.string().nullable(),
    resourceOneId: Yup.string().nullable(),
    resourceTwoId: Yup.string().nullable(),
    resourceThreeId: Yup.string().nullable(),
    resourceFourId: Yup.string().nullable(),
    solutionProposal: Yup.string().nullable(),
    companyId: Yup.string().nullable(),
    phoneNumber: Yup.string().nullable(),
    joker_char_head1: Yup.string().nullable(),
    plannedTime: Yup.number().positive().nullable(),
    quantityUnitId: Yup.string().nullable(),
    simpleTaskRespPersonlist: Yup.array().of(
      Yup.object().shape({
        userId: Yup.string().required(),
        roleName: Yup.string().required(),
      })
    ),
    resources: Yup.array().of(
      Yup.object().shape({
        resourceTwoId: Yup.string().when('resourceType', {
          is: 'RESOURCETWO',
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.nullable(),
        }),

        employeeId: Yup.string().when('resourceType', {
          is: 'EMPLOYEE',
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.nullable(),
        }),
        leader: Yup.boolean(),
        isNew: Yup.boolean(),
      })
    ),
  });

  const handleSubmit = (values) => {
    const isUpdate = !editButtonVisible;
    checkResourcePerDay(values, isUpdate);
  };

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  const valuesToForm = () => {
    const {
      simpleTaskName,
      startDate,
      endDate,
      duration,
      preparedness,
      machineId,
      projectId,
      simpleTaskTypeId,
      userId,
      resposiblePersonId,
      statusId,
      priorId,
      simpleTaskDescription,
      companyLocationId,
      resourceOneId,
      resourceTwoId,
      resourceThreeId,
      resourceFourId,
      simpleTaskRespPersonlist,
      simpleTaskAttachment,
      solutionProposal,
      companyId,
      resources,
      phoneNumber,
      joker_char_head1,
      finishedAt,
      plannedTime,
      quantityUnitId,
    } = selectedValues;

    setInitValues({
      simpleTaskName,
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
      duration,
      preparedness: preparedness ?? defaultValues.preparedness,
      machineId,
      projectId,
      projectOrderNumber: selectedValues.project?.orderNumber || '',
      simpleTaskTypeId,
      userId,
      resposiblePersonId,
      statusId,
      priorId,
      simpleTaskDescription,
      companyLocationId,
      resourceOneId,
      resourceTwoId,
      resourceThreeId,
      resourceFourId,
      solutionProposal,
      simpleTaskAttachment,
      companyId,
      joker_char_head1,
      plannedTime: plannedTime ?? defaultValues.plannedTime,
      quantityUnitId,
      finishedAt: dayjs(finishedAt),
      phoneNumber: phoneNumber ?? defaultValues.phoneNumber,
      dynamicFields: getDynVals(dynProps, selectedValues.dynamicProperties),
      simpleTaskRespPersonlist: simpleTaskRespPersonlist?.map((rp) => ({
        id: rp.respPersonListId,
        respPersonListId: rp.respPersonListId,
        userId: rp.userId,
        roleName: rp.roleName,
      })),
      resources: resources?.map((r) => ({
        ...r,
        id: r.simpleTaskId,
        resourceType: r.resourceTwoId ? ResourceType.RESOURCETWO : ResourceType.EMPLOYEE,
        isNew: false,
      })),
    });
  };

  const updateObject = (values) => {
    const updData = {
      ...values,
      duration: parseFloat(values.duration),
      preparedness: parseInt(values.preparedness),
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      plannedTime: parseFloat(values.plannedTime),
      updatedBy: user.userId,
    };

    updData.existingFiles = updData.simpleTaskAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.simpleTaskAttachment) {
      for (const file of updData.simpleTaskAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    const simpleTaskId = selectedValues.simpleTaskId;

    simpletaskService.updateSimpleTask(formData, simpleTaskId).then((simpleTask) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setRefetchTable(true);
      setEditButtonvisible(true);
      setSelectedValues(simpleTask);
      setViewField(true);
      if (dialog) {
        dialog.setTaskChanged(true);
        dialog.handleClose();
      }
    });
  };

  const checkResourcePerDay = (values, isUpdate = false) => {
    const oneResourcePerDayParam = params.SIMPLETASK_ONE_RESOURCE_PER_DAY;

    const resourcesToCheck = isUpdate ? values.resources.filter((r) => r.isNew) : values.resources;

    if (resourcesToCheck.length === 0 || oneResourcePerDayParam !== 'Y') {
      if (isUpdate) updateObject(values);
      else handleInsertObject(values);
      return;
    }

    simpletaskService
      .checkOneResourcePerDay({
        ...values,
        resources: resourcesToCheck,
      })
      .then((data) => {
        if (data.text) {
          setConfirmDialogData({
            open: true,
            message: data.text,
            handleChangeElement: () => {
              if (isUpdate) updateObject(values);
              else handleInsertObject(values);
            },
          });
        } else {
          if (isUpdate) updateObject(values);
          else handleInsertObject(values);
        }
      });
  };

  const handleInsertObject = (values) => {
    const insertObject = {
      ...values,
      duration: parseFloat(values.duration),
      preparedness: parseInt(values.preparedness),
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
      plannedTime: parseFloat(values.plannedTime),
      createdBy: user.userId,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.simpleTaskAttachment) {
      for (const file of insertObject.simpleTaskAttachment) {
        formData.append('files', file);
      }
    }

    simpletaskService.createSimpleTask(formData).then((data) => {
      addNotification({
        content: 'Sikeres mentés!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setAddField(true);
      setViewField(true);
      setSelectedValues(data);
      onCloseConfirmDialog();

      if (dialog) {
        dialog.setTaskChanged(true);
        dialog.handleClose();
      }
    });
  };

  const handleRemoveElement = async () => {
    simpletaskService.deleteSimpleTask(selectedValues.simpleTaskId).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
      if (dialog) {
        dialog.setTaskChanged(true);
        dialog.handleClose();
      }
    });
  };

  const handleAddReport = (newReport) => {
    setSelectedValues((prev) => ({
      ...prev,
      statusId: newReport.statusId,
      simpleTaskTypeId: newReport.simpleTaskTypeId,
      taskReports: newReport.taskReports,
    }));
    setRefetchTable(true);
  };

  const onAddClick = () => {
    setSelectedValues(defaultSelectedValues);
    setInitValues(defaultValues);
    setViewField(false);
    setAddField(false);
  };

  const onModifyClick = () => {
    setViewField(false);
    setEditButtonvisible(false);
  };

  const onDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const onCopyClick = () => {
    const { resources, ...taskWithoutResources } = selectedValues;

    setViewField(false);
    setAddField(false);

    setInitValues((prev) => ({
      ...prev,
      ...taskWithoutResources,
      resources: [],
      startDate: dayjs(taskWithoutResources.startDate),
      endDate: dayjs(taskWithoutResources.endDate),
    }));
  };

  const onReportClick = () => {
    setShowDialog(true);
  };

  const handleUpdateReadAt = () => {
    appService.updateReadAt(selectedValues.messages.id).then(() => {
      getRowById();
    });
  };

  const handleLoadValues = (values) => {
    setSelectedValues(values);
    setRowSelectionModel([values.id]);
  };

  const handleTemplate = () => {
    simpletaskService.getExcelTemplate().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleExport = () => {
    simpletaskService.getExcelData().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleImport = (files) => {
    simpletaskService.importExcel(files, user.userId).then(() => {
      setRefetchTable(true);
      addNotification({
        content: 'Az import sikeres!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const onCloseConfirmDialog = () => {
    setConfirmDialogData(defConfirmDialogData);
  };

  return (
    <>
      <div className="h-full overflow-auto custom-scrollbar">
        <div className="flex items-center flex-wrap justify-between">
          <div className="flex items-center">
            <FormHeaderUI
              addClick={(!dialog || !dialog.isViewMode) && (!dialog ? onAddClick : null)}
              addDisabled={dialog?.isViewMode || !viewField || !canEdit}
              modClick={
                (!dialog || !dialog.isViewMode || dialog.isUpdate) &&
                (!dialog || dialog.isUpdate ? onModifyClick : null)
              }
              modDisabled={dialog?.isViewMode || !viewField || operatorsDisabled || !canEdit}
              deleteClick={
                (!dialog || !dialog.isViewMode || dialog.isUpdate) &&
                (!dialog || dialog.isUpdate ? onDeleteClick : null)
              }
              deleteDisabled={
                dialog?.isViewMode || (!dialog?.editing && !viewField) || operatorsDisabled || !canEdit || !canDelete
              }
              copyClick={
                (!dialog || !dialog.isViewMode || dialog.isUpdate) && (!dialog || dialog.isUpdate ? onCopyClick : null)
              }
              copyDisabled={dialog?.isViewMode || !viewField || operatorsDisabled || !canEdit || !canCopy}
            />
            {!dialog && (
              <ButtonUI
                text="Lejelentés"
                size="sm"
                className="bg-labelColor py-3"
                onClick={onReportClick}
                disabled={!viewField || operatorsDisabled}
              />
            )}
          </div>
          <div>
            {(!dialog || dialog.isUpdate) && (
              <History disabled={operatorsDisabled} onClick={() => setHistoryDialogOpen(!historyDialogOpen)} />
            )}
          </div>
        </div>

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ resetForm, setFieldValue }) => (
            <Form>
              <FormFields
                viewField={dialog?.isViewMode || ((!dialog || dialog?.isUpdate) && viewField)}
                submenuId={submenu}
                selectedValues={selectedValues}
                params={params}
                submenuNames={submenuNames}
                autocompleteOptions={autocompleteOptions}
                roles={roles}
                editButtonVisible={editButtonVisible}
                autocompleteData={autocompleteData}
                dynProperties={{ dynProps, setDynProps }}
              />

              <SimpleTaskTabs
                disabled={dialog?.isViewMode || ((!dialog || dialog?.isUpdate) && viewField)}
                setRefetchTable={setRefetchTable}
                addField={(!dialog || dialog?.isUpdate) && addField}
                selectedValues={selectedValues}
                user={user}
                autocompleteData={autocompleteData}
                autocompleteOptions={autocompleteOptions}
                defaultField={defaultRespPerson}
                defaultResource={defaultResource}
                handleLoadValues={handleLoadValues}
                setSelectedValues={setSelectedValues}
                dynLoadingState={{ dynLoading, setDynLoading }}
                dynProperties={{ dynProps, setDynProps }}
                setDefaultValues={setDefaultValues}
                setFieldValue={setFieldValue}
                submenuId={submenu}
                handleImport={handleImport}
                handleExport={handleExport}
                handleTemplate={handleTemplate}
                dialog={dialog}
              />

              <FormButtonPanelUI
                onCancelClick={() => {
                  setViewField(true);
                  setEditButtonvisible(true);
                  setAddField(true);
                  resetForm();
                  if (dialog) {
                    dialog.handleClose();
                  }
                }}
                disabled={
                  dialog
                    ? dialog.isViewMode ||
                      (dialog.isUpdate && ((editButtonVisible && addField) || (!editButtonVisible && !addField)))
                    : (editButtonVisible && addField) || (!editButtonVisible && !addField)
                }
              />
            </Form>
          )}
        </Formik>
      </div>

      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />

      <DialogUI
        open={showDialog}
        headerContent={'Feladat lejelentő'}
        fullWidth={true}
        onClose={() => setShowDialog(false)}
      >
        <TaskReportForm selectedTask={selectedValues} setShowDialog={setShowDialog} onReportAdd={handleAddReport} />
      </DialogUI>

      <HistoryDialog
        open={historyDialogOpen}
        handleClose={() => {
          setHistoryDialogOpen(false);
        }}
        formId={submenu}
        lineId={selectedValues.simpleTaskId}
        fieldConfig={[]}
        isDateTimeFormat={true}
      />

      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog()}
        message={confirmDialogData.message}
      />
    </>
  );
};
